/**
 * Delivery windows — a 1:1 port of Android's `DeliveryWindow.kt`.
 *
 * The grid is whatever the city sent (`GET /v1/consumer/service-area` → `delivery_windows`). The
 * client does no arithmetic on window boundaries at all: `from`/`to` come in as RFC3339 and go
 * back out as `scheduled_from`/`scheduled_to` **verbatim**.
 *
 * That rule was learnt the hard way. The app used to compute the boundaries itself from a
 * hard-coded 09:00–13:00 / 13:00–17:00 / 17:00–21:00 grid while the backend accepted any pair of
 * instants, so «Завтра, 09:00–13:00» could sit above a window of 03:00–03:05 (audit 2026-08-21).
 * The backend now refuses (400) a window the city does not offer, which would turn the same
 * mistake into a rejection on the payment screen.
 */
import type { OrderDraft } from './draft';

/** Windows live by Moscow wall-clock: the city promises «с девяти до девяти» in its own time. */
const MOSCOW_TIME_ZONE = 'Europe/Moscow';

/** Russian month names regardless of the device locale. */
const DAY_MONTH = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  timeZone: MOSCOW_TIME_ZONE,
});

export interface DeliveryWindow {
  /** RFC3339. Sent back untouched. */
  from: string;
  /** RFC3339. Sent back untouched. */
  to: string;
  /** 0 today, 1 tomorrow. The server numbers the day; the app names it. */
  dayOffset: number;
  /** «09:00–13:00», Moscow hours, ready to render as-is. */
  label: string;
  /**
   * Until when the window still takes a booking (RFC3339): its end minus the spot's cutoff —
   * 17:00–21:00 is not offered at 20:30, that would be «Ближайшая» half an hour before closing
   * (owner decision 30.09.2026). Absent from a backend that predates it; then the window lives
   * until its end, as before.
   */
  bookableUntil?: string | null;
}

function windowInstant(rfc3339: string): Date | null {
  const parsed = new Date(rfc3339);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * A window is alive until its `bookableUntil`, not until it starts.
 *
 * At 10:00 a person is standing inside 09:00–13:00, and refusing them would take away the window
 * they are currently in — but at 12:30 only half an hour of it is left, and the server stops taking
 * it an hour before its end. Same rule as the server's `WindowGrid::offer`, so the two cannot
 * disagree about what is still bookable. A backend that sends no `bookable_until` keeps the old
 * rule: until the window ends.
 *
 * An unparseable time is never offered: a time that cannot be read cannot be checked, and
 * promising it is worse than not offering it.
 */
export function isWindowOfferable(window: DeliveryWindow, now: Date = new Date()): boolean {
  const end = windowInstant(window.bookableUntil ?? window.to);
  if (end === null) return false;
  return end.getTime() > now.getTime();
}

/**
 * The windows still choosable.
 *
 * The server no longer sends windows that have passed, but the grid lives on the draft, and the
 * draft outlives the evening: without this filter somebody returning to their order at nine at
 * night would be shown the morning window and would leave with it (audit 2026-08-18).
 */
export function offerableWindows(
  windows: DeliveryWindow[],
  now: Date = new Date(),
): DeliveryWindow[] {
  return windows.filter((w) => isWindowOfferable(w, now));
}

/**
 * «Сегодня» / «Завтра» / «Послезавтра».
 *
 * The booking horizon is three days; the dated branch exists in case a city widens it, and then
 * the day is named by its own Moscow date.
 */
export function deliveryDayLabel(window: DeliveryWindow): string {
  if (window.dayOffset === 0) return 'Сегодня';
  if (window.dayOffset === 1) return 'Завтра';
  if (window.dayOffset === 2) return 'Послезавтра';
  const start = windowInstant(window.from);
  return start === null ? 'Позже' : DAY_MONTH.format(start);
}

/** «Завтра, 09:00–13:00» — for the summary and for `desired_slot_label`. */
export function deliveryWindowSummary(window: DeliveryWindow): string {
  return `${deliveryDayLabel(window)}, ${window.label}`;
}

/**
 * Puts the city's grid on the draft, and decides the fate of an already-chosen window.
 *
 * The choice survives a refresh only if the server sent **the same** window — compared on
 * `from`/`to`, the only thing the server itself later checks — and it survives as the *fresh*
 * copy, because overnight the same window's `dayOffset` goes from 1 to 0 and the label under it
 * has to change with it.
 *
 * A pin that moved to another city replaces the grid wholesale, and the chosen window simply
 * disappears. The alternative is reaching the payment screen and being told «такого окна доставки
 * в этом городе нет».
 */
export function withCityWindows(
  draft: OrderDraft,
  windows: DeliveryWindow[],
  dispatchLeadMinutes: number | null,
): OrderDraft {
  const chosen = draft.selectedWindow;
  const stillOffered =
    chosen === null
      ? null
      : (windows.find((w) => w.from === chosen.from && w.to === chosen.to) ?? null);

  return {
    ...draft,
    deliveryWindows: windows,
    dispatchLeadMinutes,
    selectedWindow: stillOffered,
  };
}
